import React, { useState } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useMotionValueEvent,
} from "framer-motion";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";

/**
 * Estilos CSS Mesclados e Ampliados: Glassmorphism + Shimmer + Revelação de Ícone
 * Adaptados para o tema Templário (Cores Gold/Crimson/Preto)
 */
const customStyles = `
  .glass-container {
    background: rgba(6, 5, 7, 0.85);
    backdrop-filter: blur(25px) saturate(200%);
    -webkit-backdrop-filter: blur(25px) saturate(200%);
    border: 1px solid rgba(201, 168, 76, 0.2);
    box-shadow: 
      0 10px 25px -5px rgba(0, 0, 0, 0.5), 
      0 8px 10px -6px rgba(0, 0, 0, 0.5),
      inset 0 1px 1px rgba(201, 168, 76, 0.1);
  }

  /* --- EFEITO DE REVELAÇÃO DE ÍCONE --- */
  .optn:hover .nav-text {
    color: #c9a84c !important;
    transition: 0.5s;
  }
  
  .optn .nav-item-inner {
    transition: 0.5s;
    padding: 0px !important;
    padding-left: 20px !important;
    padding-right: 20px !important;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .optn:hover .nav-item-inner {
    transition: 0.5s;
    padding: 8px !important;
    padding-left: 20px !important;
    padding-right: 20px !important;
  }

  .optn .nav-icon {
    opacity: 0;
    width: 0px;
    transition: 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    transform: scale(0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    color: #c9a84c;
  }

  .optn:hover .nav-icon {
    opacity: 1;
    width: 24px;
    transition: 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    transform: scale(1.1);
    margin-right: 10px;
  }

  /* Estilos do Botão Shimmer Ampliado */
  .bookmarkBtn {
    width: auto;
    min-width: 130px;
    height: 44px;
    border-radius: 50px;
    border: 1px solid rgba(201, 168, 76, 0.4);
    background-color: #0c0c0c;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    cursor: none;
    transition-duration: 0.3s;
    overflow: hidden;
    padding: 0;
    position: relative;
    margin-left: 10px;
  }

  .bookmarkBtn::before {
    content: "";
    position: absolute;
    inset: -1px;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(201, 168, 76, 0.2) 25%, 
      #c9a84c 50%, 
      rgba(201, 168, 76, 0.2) 75%, 
      transparent 100%);
    background-size: 200% 100%;
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    opacity: 0;
    z-index: 5;
    transition: opacity 0.3s;
  }

  .bookmarkBtn:hover::before {
    opacity: 1;
    animation: shinerySync 2s infinite ease-in-out;
  }

  .IconContainer {
    width: 32px;
    height: 32px;
    background: linear-gradient(to bottom, #f2d478, #c9a84c);
    border-radius: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    z-index: 2;
    transition-duration: 0.3s;
    flex-shrink: 0;
    margin-left: 6px;
    position: relative;
  }

  .bookmarkBtn:hover .IconContainer {
    width: calc(100% - 12px);
    border-radius: 40px;
  }

  @keyframes shinerySync {
    0% { background-position: 150% 0; }
    100% { background-position: -150% 0; }
  }

  .btn-icon-svg {
    width: 16px;
    height: 16px;
    color: white;
    transition: 0.3s;
  }

  .bookmarkBtn:hover .btn-icon-svg {
    animation: arrowNudge 1s infinite ease-in-out;
  }

  @keyframes arrowNudge {
    0%, 100% { transform: translateX(0); }
    50% { transform: translateX(5px); }
  }

  .btn-text {
    color: #c9a84c;
    z-index: 1;
    transition-duration: 0.3s;
    font-family: 'Times New Roman', Times, serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    margin-left: 12px;
    margin-right: 15px;
  }

  .bookmarkBtn:hover .btn-text {
    opacity: 0;
    transform: translateX(40px);
  }
`;

export const FloatingNav = ({
  navItems,
  className
}: {
  navItems: {
    name: string;
    link: string;
    icon?: React.ReactNode;
  }[];
  className?: string;
}) => {
  const { scrollY } = useScroll();
  const [visible, setVisible] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useTranslation();

  useMotionValueEvent(scrollY, "change", (current) => {
    setVisible(true);
  });

  return (
    <>
      <style>{customStyles}</style>
      <AnimatePresence mode="wait">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: -20 }}
          animate={{
            y: visible ? 0 : -100,
            opacity: visible ? 1 : 0,
            scale: visible ? 1 : 0.9,
          }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 30,
          }}
          className={cn(
            "flex w-[95%] lg:w-auto max-w-fit fixed top-4 lg:top-6 inset-x-0 mx-auto rounded-full z-[5000] px-4 lg:pr-6 lg:pl-4 py-2 items-center justify-between lg:justify-center shadow-2xl",
            "glass-container",
            className
          )}>

          {/* Mobile Branding Text */}
          <div className="flex lg:hidden items-center">
            <span className="text-[11px] font-bold text-[#c9a84c] tracking-widest uppercase font-title leading-tight">
              {t('nav.brand', 'Encontro Nacional Templário')}
            </span>
          </div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-[#c9a84c] hover:bg-[#c9a84c]/10 rounded-full transition-colors"
            aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            {isMenuOpen ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6 6 18M6 6l12 12" /></svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M4 12h16M4 6h16M4 18h16" /></svg>
            )}
          </button>

          {/* Desktop Nav Items */}
          <div className="hidden lg:flex items-center">
            {navItems.map((navItem, idx) => (
              <a
                key={`link-${idx}`}
                href={navItem.link}
                onMouseEnter={() => setHoveredIndex(idx)}
                onMouseLeave={() => setHoveredIndex(null)}
                className="optn relative group"
              >
                <div className="nav-item-inner">
                  <span className="nav-icon">
                    {navItem.icon}
                  </span>

                  <span className="nav-text text-neutral-400 text-[10px] font-bold uppercase tracking-widest font-title transition-colors duration-300 whitespace-nowrap">
                    {navItem.name}
                  </span>

                  {hoveredIndex === idx && (
                    <motion.span
                      layoutId="navUnderline"
                      initial={{ scaleX: 0, opacity: 0 }}
                      animate={{ scaleX: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      className="absolute bottom-[-2px] left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent origin-center shadow-[0_0_12px_rgba(201,168,76,0.8)]"
                    />
                  )}
                </div>
              </a>
            ))}
          </div>


        </motion.div>
      </AnimatePresence>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-[4999] bg-[#060507] lg:hidden pt-24 px-6"
          >
            <div className="flex flex-col gap-4">
              {navItems.map((navItem, idx) => (
                <motion.a
                  key={`mobile-link-${idx}`}
                  href={navItem.link}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-2xl group active:bg-[#c9a84c]/20 transition-all"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-[#c9a84c] opacity-70">{navItem.icon}</span>
                    <span className="text-sm font-bold text-neutral-200 uppercase tracking-widest">{navItem.name}</span>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c9a84c" strokeWidth="2.5"><path d="m9 18 6-6-6-6" /></svg>
                </motion.a>
              ))}


            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
